
import { Typography, Divider, Link, Icon } from '@shc/ui';
import NextImage from '@components/next-image'

import sortBy from 'lodash/sortBy'

import type { Hit } from 'instantsearch.js'
import { calculateDistance, type Coordinates } from '@lib/utilities/location'
import { formatDistance, getPrimaryPlace, getProviderImageSrc } from '@lib/utilities/search'

import { PROVIDER_PLACEHOLDER_IMAGE_URL } from '@lib/constants'

type Specialty = {
  specialty_name: string;
  specialty_id: number;
  is_primary: boolean;
  is_referable: boolean;
  is_board_certified: boolean;
  is_super_specialist: boolean;
  is_accepts_consults: boolean;
};

type MedicalGroup = {
  id: number;
  name: string;
  url: string | null;
};

type Location = {
  id: number;
  name: string;
  street_address: string;
  secondary_address: string | null;
  city: string;
  state: string;
  postal_code: string;
  phone: string;
  fax: string;
  phone_formatted?: string;
  fax_formatted?: string;
  lat?: string | null;
  lon?: string | null;
  distance?: number;
  tags?: Array<{ name?: string }>;
}

type TypeProviderHit = {
  id: number;
  slug: string;
  photo_url: string | null;
  name_formatted_with_degrees: string;
  specialties: Specialty[];
  medical_groups: MedicalGroup[];
  is_referable: boolean;
  name_formatted: string;
  places: Location[];
  provider_types: string[]
  options: string[];
  video_urls?: string[] | null;
  insurances_facet?: string[];
  distance?: number;
  geo_distance?: number;
  _rankingInfo?: {
    geoDistance?: number;
  };
}

export type ProviderSearchResultProps = {
  hit: Hit<TypeProviderHit>
  site?: 'public' | 'providers'
  aroundLatLng?: string
}

function parseAroundLatLng(aroundLatLng?: string): Coordinates | null {
  if (!aroundLatLng) return null

  const [lat, lng] = aroundLatLng.split(',').map((value) => Number(value.trim()))
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null

  return { lat, lng }
}

function formatPhoneNumber(phone?: string): string | null {
  if (!phone) return null

  const digits = phone.replace(/\D/g, '')
  if (digits.length !== 10) return phone

  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
}

function getPlacesWithDistance(places: Location[], searchCoords: Coordinates | null): Location[] {
  if (!Array.isArray(places)) return []

  return places.map((place) => {
    const lat = Number(place.lat)
    const lng = Number(place.lon)

    if (!searchCoords || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      return place
    }

    return {
      ...place,
      distance: calculateDistance(searchCoords, { lat, lng }),
    }
  })
}

function getClosestPlace(placesWithDistance: Location[], searchCoords: Coordinates | null): Location | null {
  if (!searchCoords) return null

  return placesWithDistance
    .filter((place) => typeof place.distance === 'number')
    .reduce<Location | null>((closest, place) => {
      if (!closest) return place

      return (place.distance ?? Number.POSITIVE_INFINITY) < (closest.distance ?? Number.POSITIVE_INFINITY)
        ? place
        : closest
    }, null)
}

function formatMilesAway(distanceText: string): string {
  const trimmedDistance = distanceText.trim()

  if (trimmedDistance.endsWith(' mi')) {
    return `${trimmedDistance.slice(0, -3)} miles away`
  }

  if (trimmedDistance.endsWith('mi')) {
    return `${trimmedDistance.slice(0, -2).trimEnd()} miles away`
  }

  return `${trimmedDistance} miles away`
}

function getDistanceText({
  closestPlace,
  searchCoords,
  displayPlace,
  hit,
}: {
  closestPlace: Location | null
  searchCoords: Coordinates | null
  displayPlace: Location | null
  hit: Hit<TypeProviderHit>
}): string | null {
  if (closestPlace) {
    const citySuffix = closestPlace.city ? ` · ${closestPlace.city}` : ''
    return `${closestPlace.distance?.toFixed(1)} miles away${citySuffix}`
  }

  if (!searchCoords) return null

  const fallbackDistance = formatDistance(hit)
  if (!fallbackDistance) return null

  const citySuffix = displayPlace?.city ? ` · ${displayPlace.city}` : ''
  return `${formatMilesAway(fallbackDistance)}${citySuffix}`
}

const ProviderSearchResult = ({ hit, site = 'public', aroundLatLng }: ProviderSearchResultProps) => {
  const {
    name_formatted_with_degrees,
    slug,
    specialties,
    name_formatted,
    places,
    insurances_facet,
  } = hit
  const profileLink = site === 'public' ? '/doctors/' : '/providers/search/doctors/'
  const profileHref = profileLink + slug
  const locationsHref = `${profileHref}#provider-locations`
  const searchCoords = parseAroundLatLng(aroundLatLng)
  const placesWithDistance = getPlacesWithDistance(places, searchCoords)
  const closestPlace = getClosestPlace(placesWithDistance, searchCoords)
  const displayPlace = (closestPlace ?? getPrimaryPlace({ ...hit, places: placesWithDistance })) as Location | null
  const distanceText = getDistanceText({ closestPlace, searchCoords, displayPlace, hit })
  const acceptsNewPatients = Boolean(insurances_facet?.length)
  const locationCount = places?.length ?? 0
  const phoneLabel = displayPlace?.phone_formatted ?? formatPhoneNumber(displayPlace?.phone)
  const phoneHref = displayPlace?.phone ? `tel:+1${displayPlace.phone.replace(/\D/g, '')}` : null
  const providerPhotoSrc = getProviderImageSrc(hit)
  const providerImageSrc = providerPhotoSrc ?? PROVIDER_PLACEHOLDER_IMAGE_URL
  const showPracticeName = Boolean(displayPlace?.name && displayPlace.name !== displayPlace.street_address)
  const showAddress = Boolean(displayPlace?.street_address)
  const providerHeading = name_formatted_with_degrees
  const showMedia = site === 'public'
  const showPatientStatus = site === 'public'

  return (
    <div key={hit.id} className='search-result-doctor'>
      <div className="pt-8 pb-8">
        <div className="flex flex-col md:flex-row gap-4 md:gap-8">
          {showMedia && (
            <div className="flex flex-row md:flex-col gap-4 items-center">
              <div className="relative outline-primary outline-2 outline-offset-2 rounded-full">
                <Link href={profileHref} noUnderline aria-label={`View profile for ${name_formatted}`}>
                  <div className="h-[88px] w-[88px] overflow-hidden rounded-full">
                    <NextImage
                      src={providerImageSrc}
                      aria-label={`${providerHeading} [Image]`}
                      alt={`Image of ${name_formatted}`}
                      width={88}
                      height={88}
                      sizes="88px"
                      className="h-[88px] w-[88px] object-cover object-top scale-100 hover:scale-105 duration-300 hover:duration-200 ease-in-out"
                    />
                  </div>
                </Link>
              </div>
              <div className="flex md:hidden flex-col justify-start items-start">
                <Link href={profileHref} noUnderline className="text-tertiary hover:text-linkhover hover:underline">
                  <Typography variant="h4" as="p" className="text-[1.25rem] font-bold text-tertiary hover:text-linkhover">{providerHeading}</Typography>
                </Link>
              </div>
            </div>
          )}

          <div className='flex flex-col md:flex-row md:justify-between gap-4 w-full'>
            <div className="flex flex-col gap-2">
              {!showMedia && (
                <div className="flex md:hidden flex-col justify-start items-start">
                  <Link href={profileHref} noUnderline className="text-tertiary hover:text-linkhover hover:underline">
                    <Typography variant="h4" as="p" className="text-[1.25rem] leading-[1.875rem] font-bold text-tertiary hover:text-linkhover">{providerHeading}</Typography>
                  </Link>
                </div>
              )}
              <div className="hidden md:flex flex-row gap-2">
                <Link href={profileHref} noUnderline className="text-tertiary hover:text-linkhover hover:underline">
                  <Typography variant="h3" as="p" className="text-[1.25rem] leading-[1.875rem] font-bold text-tertiary hover:text-linkhover">{providerHeading}</Typography>
                </Link>
              </div>

              <div className="flex flex-col md:flex-row md:justify-between gap-4">
                <div className="flex flex-col gap-4">
                  {!!specialties?.length && (
                    <div className="flex flex-col">
                      {sortBy(specialties, ['specialty_name']).map((specialty) => {
                        return (
                          <Typography key={specialty.specialty_name} variant="body-semibold">
                            {specialty.specialty_name}
                          </Typography>
                        )
                      })}
                    </div>
                  )}

                  {showPatientStatus && (
                    <Typography variant="body" className="flex items-center gap-2">
                      <Icon
                        className={acceptsNewPatients ? 'h-4 text-success' : 'h-4 text-danger'}
                        icon={acceptsNewPatients ? 'check' : 'xmark'}
                      />
                      {acceptsNewPatients ? 'Accepting new patients' : 'Not accepting new patients'}
                    </Typography>
                  )}

                  {distanceText && (
                    <Typography variant="body" className="flex items-center gap-2 text-gray-700">
                      <Icon className="h-4 text-tertiary" icon="location-dot" />
                      {distanceText}
                    </Typography>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-1 min-w-[215px] max-w-[310px] flex-grow">
              {!!displayPlace && (
                <>
                  <div className="space-y-0">
                    <div className='mb-4'>
                      {showPracticeName && (
                        <Typography variant="body-semibold">{displayPlace.name}</Typography>
                      )}
                      {showAddress && (
                        <>
                          <Typography>{displayPlace.street_address}</Typography>
                          {displayPlace.secondary_address && (
                            <Typography>{displayPlace.secondary_address}</Typography>
                          )}
                          <Typography>
                            {displayPlace.city}, {displayPlace.state}{' '}
                            {displayPlace.postal_code}
                          </Typography>
                        </>
                      )}
                    </div>

                    {phoneLabel && phoneHref && (
                      <Typography>
                        <Link
                          href={phoneHref}
                          noUnderline
                          className="text-tertiary hover:text-linkhover"
                        >
                          {phoneLabel}
                        </Link>
                      </Typography>
                    )}
                    {displayPlace.fax_formatted && (
                      <Typography>Fax: {displayPlace.fax_formatted}</Typography>
                    )}
                    {!displayPlace.fax_formatted && displayPlace.fax && (
                      <Typography>Fax: {displayPlace.fax}</Typography>
                    )}
                  </div>
                  <div className="space-y-0">
                    <Link
                      href={locationsHref}
                      noUnderline
                      className="text-tertiary hover:text-linkhover"
                    >
                      {locationCount > 1 && (`${locationCount} Locations`)} 
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <Divider />
    </div>
  )
}

export default ProviderSearchResult